import { useMemo, useState } from 'react';
import { Badge, ErrorMessage, Field, Loading, Modal, SearchSelect, useAsync } from '../../components/ui';
import { IconTransfer, IconTruck } from '../../components/icons';
import { api } from '../../lib/api';
import { formatNumber } from '../../lib/format';

/**
 * Umbuchung zwischen Lagern – im Alltag „Lagerentnahme nach Fahrzeug“ (Kap. 7).
 *
 * Der häufigste Fall ist voreingestellt: aus dem Lager mit Bestand hinaus, in
 * ein Fahrzeuglager hinein. Wer stattdessen ins Objektlager oder zurück ins
 * Zentrallager bucht, ändert einfach das Ziel.
 *
 * Der aktuelle Bestand steht an beiden Lagern dabei. Eine Umbuchung, bei der
 * man die Ausgangsmenge nicht sieht, wird geraten – und geratene Mengen sind
 * genau die Fehlbuchungen, die die Bestandskontrolle später als Negativbestand
 * meldet.
 */
export function TransferModal({
  article,
  warehouses,
  onClose,
  onSaved,
}: {
  article: any;
  warehouses: any[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const locations = useAsync(() => api.get(`/api/material/articles/${article.id}/locations`), [article.id]);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const stockByWarehouse = useMemo(() => {
    const map = new Map<number, number>();
    for (const entry of locations.data ?? []) map.set(entry.id, entry.quantity);
    return map;
  }, [locations.data]);

  // Vorbelegung, sobald die Bestände da sind: Quelle ist das Lager mit dem
  // größten Bestand, Ziel das erste Fahrzeuglager.
  const suggestedFrom =
    fromId ||
    String(
      [...(locations.data ?? [])].sort((a: any, b: any) => b.quantity - a.quantity).find((entry: any) => entry.quantity > 0)
        ?.id ?? warehouses.find((warehouse) => warehouse.kind === 'central')?.id ?? warehouses[0]?.id ?? '',
    );
  const suggestedTo = toId || String(warehouses.find((warehouse) => warehouse.kind === 'vehicle')?.id ?? '');

  const available = stockByWarehouse.get(Number(suggestedFrom)) ?? 0;
  const amount = Number(quantity);
  const exceeds = amount > available;
  const valid = suggestedFrom && suggestedTo && suggestedFrom !== suggestedTo && amount > 0;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/material/transfer', {
        fromWarehouseId: Number(suggestedFrom),
        toWarehouseId: Number(suggestedTo),
        articleId: article.id,
        quantity: amount,
        note: note || null,
        // Der Hinweis stand im Dialog; wer trotzdem bucht, meint es so.
        allowNegative: exceeds,
      });
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const label = (warehouse: any) => {
    const stock = stockByWarehouse.get(warehouse.id) ?? 0;
    return `${warehouse.name} (${formatNumber(stock, 2)} ${article.unit ?? ''})`.trim();
  };

  return (
    <Modal
      title={`Umbuchen – ${article.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" onClick={submit} disabled={!valid || busy}>
            <IconTransfer width={14} height={14} /> {exceeds ? 'Trotzdem buchen' : 'Umbuchen'}
          </button>
        </>
      }
    >
      {locations.loading ? (
        <Loading />
      ) : (
        <div className="stack">
          <ErrorMessage error={error} />

          <div className="form-grid">
            <Field label="Von Lager" required>
              <SearchSelect
                value={String(suggestedFrom ?? '')}
                onChange={(wert) => setFromId(wert)}
                options={(warehouses ?? []).map((warehouse: any) => ({ value: String(warehouse.id), label: String(label(warehouse)) }))}
                clearable={false}
              />
            </Field>
            <Field label="Nach Lager" required hint="Fahrzeuglager entsprechen dem Kennzeichen des Fahrzeugs.">
              <SearchSelect
                value={String(suggestedTo ?? '')}
                onChange={(wert) => setToId(wert)}
                options={warehouses
                .filter((warehouse) => String(warehouse.id) !== suggestedFrom)
                .map((warehouse: any) => ({ value: String(warehouse.id), label: String(warehouse.kind === 'vehicle' ? `🚚 ${label(warehouse)}` : label(warehouse)) }))}
                placeholder="– bitte wählen –"
              />
            </Field>
            <Field label="Menge" required>
              <input
                type="number"
                step="0.01"
                min="0"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                autoFocus
              />
            </Field>
            <Field label="Bemerkung">
              <input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="z. B. Bestückung für Tour KW 33"
              />
            </Field>
          </div>

          {exceeds && (
            <div className="alert warning">
              Im Quelllager sind nur {formatNumber(available, 2)} {article.unit} vorhanden. Wird trotzdem gebucht,
              entsteht ein Negativbestand – die Bestandskontrolle weist ihn anschließend aus.
            </div>
          )}

          <span className="section-title">Aktuelle Verteilung</span>
          {(locations.data ?? []).length === 0 ? (
            <span className="small subtle">Dieser Artikel liegt derzeit in keinem Lager.</span>
          ) : (
            <div className="stack" style={{ gap: 6 }}>
              {(locations.data ?? []).map((entry: any) => (
                <div key={entry.id} className="row between">
                  <span className="row" style={{ gap: 6 }}>
                    {entry.kind === 'vehicle' && <IconTruck width={14} height={14} />}
                    {entry.name}
                    {entry.vehicle_plate && <span className="chip tiny">{entry.vehicle_plate}</span>}
                    {entry.first_name && <span className="small subtle">{entry.first_name} {entry.last_name}</span>}
                    {entry.property_name && <span className="small subtle">{entry.property_name}</span>}
                  </span>
                  <Badge variant={entry.quantity < 0 ? 'danger' : entry.kind === 'vehicle' ? 'warning' : 'success'}>
                    {formatNumber(entry.quantity, 2)} {article.unit}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
